import React, { useState, useEffect, useMemo, lazy, Suspense, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronRight, FiSearch, FiX } from 'react-icons/fi';
import { useBannerImages } from '../hooks/useBannerImages';
import { useCategories } from '../hooks/useCategories';
import { useBrands } from '../hooks/useBrands';
import { useProducts } from '../hooks/useProducts';
import { useAllSubcategories } from '../hooks/useSubcategories';
import { useLanguage } from '../context/LanguageContext';
import { slugify } from '../utils/slugify';
import { fetchSalesDataPublicSafe } from '../utils/productSorting';

// Lazy load components for better initial load
const ProductCard = lazy(() => import('../components/ProductCard'));
const CategoryCard = lazy(() => import('../components/CategoryCard'));
const SubcategoryCard = lazy(() => import('../components/SubcategoryCard'));

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const BannerContainer = styled.section`
  position: relative;
  margin-bottom: 48px;
  border-radius: 16px;
  overflow: hidden;
  height: 400px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  background: #1f2937;
  /* Isolate banner from page RTL so slides stay visually centered */
  direction: ltr;

  @media (max-width: 1024px) and (min-width: 769px) {
    height: auto;
    min-height: 0;
    max-height: none;
    padding: 0;
    margin-top: 12px;
    margin-bottom: 40px;
    border-radius: 14px;
    overflow: hidden;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 0;
    padding: 0;
    /* Extra space below fixed header + category bar on mobile */
    margin-top: 28px;
    margin-bottom: 32px;
    border-radius: 12px;
    overflow: hidden;
  }

  @media (max-width: 480px) {
    margin-top: 32px;
    margin-bottom: 24px;
    border-radius: 8px;
  }
`;

/* Stack all slides in one box so two <img> nodes never sit side-by-side in RTL/flex */
const BannerSlideStack = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  align-self: stretch;
  box-sizing: border-box;
  background: linear-gradient(
    90deg,
    #f3f4f6 0%,
    #e5e7eb 50%,
    #f3f4f6 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;

  @media (max-width: 1024px) {
    height: auto;
    /* 1200×400 (3:1) — height follows width only (no extra min-height = no empty frame) */
    aspect-ratio: 3 / 1;
  }
`;

const BannerLink = styled.a`
  width: 100%;
  height: 100%;
  display: block;
  cursor: ${props => props.hasUrl ? 'pointer' : 'default'};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  text-decoration: none;
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  position: absolute;
  inset: 0;
  opacity: ${props => props.active ? 1 : 0};
  transition: opacity 0.5s ease-in-out;
  will-change: opacity;
  pointer-events: none;
`;

const BannerDots = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 2;

  @media (max-width: 768px) {
    bottom: 14px;
  }

  @media (max-width: 480px) {
    bottom: 12px;
  }
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: white;
  }
`;

const Section = styled.section`
  margin-bottom: 64px;

  @media (max-width: 768px) {
    margin-bottom: 48px;
  }

  @media (max-width: 480px) {
    margin-bottom: 32px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  position: relative;
  flex-direction: row-reverse;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;
  }
`;

const ViewAllWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ViewAllWrapperMobile = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    display: flex;
    margin-top: 24px;
  }
  
  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;


const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 800;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: left;
  margin: 0;
  position: relative;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 28px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: left;
  margin-top: 12px;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 14px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-top: 8px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ViewAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #5efba9 0%, #34d399 100%);
  color: #0f172a;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  border-radius: 50px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(94, 251, 169, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(94, 251, 169, 0.4);
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 10px 20px;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 16px;
    padding: 12px 20px 20px 20px;
    margin: 0 -20px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-padding: 20px;
    
    & > * {
      flex: 0 0 180px;
      min-width: 180px;
      max-width: 180px;
    }
    
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 480px) {
    gap: 14px;
    padding: 12px 16px 20px 16px;
    margin: 0 -16px;
    scroll-padding: 16px;
    
    & > * {
      flex: 0 0 160px;
      min-width: 160px;
      max-width: 160px;
    }
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 16px;
    padding: 12px 20px 20px 20px;
    margin: 0 -20px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-padding: 20px;
    
    & > * {
      flex: 0 0 240px;
      min-width: 240px;
      max-width: 240px;
    }
    
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 480px) {
    gap: 14px;
    padding: 12px 16px 20px 16px;
    margin: 0 -16px;
    scroll-padding: 16px;
    
    & > * {
      flex: 0 0 200px;
      min-width: 200px;
      max-width: 200px;
    }
  }
`;

const SearchContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto 32px;
  padding: 0 20px;
  position: relative;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 16px;
  gap: 12px;
  transition: all 0.3s ease;
  
  &:focus-within {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const SearchIcon = styled(FiSearch)`
  color: #9ca3af;
  font-size: 20px;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #1f2937;
  background: transparent;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #6b7280;
  }
`;

const SearchResults = styled.div`
  margin-top: 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const BrandsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 16px;
    padding: 8px 20px 16px 20px;
    margin: 0 -20px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;
    
    /* Gradient fade indicators */
    &::before,
    &::after {
      content: '';
      position: sticky;
      top: 0;
      width: 20px;
      height: 100%;
      z-index: 1;
      pointer-events: none;
      flex-shrink: 0;
    }
    
    &::before {
      left: 0;
      background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
    }
    
    &::after {
      right: 0;
      background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
    }
    
    & > * {
      flex: 0 0 180px;
      min-width: 180px;
      max-width: 180px;
    }
    
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 480px) {
    gap: 14px;
    padding: 8px 16px 16px 16px;
    margin: 0 -16px;
    
    &::before,
    &::after {
      width: 16px;
    }
    
    & > * {
      flex: 0 0 160px;
      min-width: 160px;
      max-width: 160px;
    }
  }
`;

const BrandCard = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  text-decoration: none;
  color: inherit;
  will-change: transform;

  &:hover {
    transform: translateY(-4px);
    border-color: #5efba9;
    box-shadow: 0 8px 16px rgba(94, 251, 169, 0.2);
  }

  @media (max-width: 768px) {
    padding: 14px;
    gap: 10px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1.5px solid #f1f5f9;
    height: 100%;
    display: flex;
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    }
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 8px;
    border-radius: 10px;
    
    &:hover {
      transform: none;
    }
  }
`;

const BrandImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  will-change: transform;

  ${BrandCard}:hover & {
    transform: scale(1.05);
  }
`;

const BrandImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
  loading: lazy;
`;

const BrandIcon = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
`;

const BrandName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const CategoryBar = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%);
  border: 1px solid rgba(94, 251, 169, 0.2);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.8);
  position: relative;
  margin-bottom: 40px;
  border-radius: 16px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #5efba9 0%, #34d399 50%, #5efba9 100%);
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @media (max-width: 768px) {
    margin-bottom: 32px;
    border-radius: 12px;
  }
`;

const CategoryContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  @media (max-width: 768px) {
    padding: 10px 16px;
    gap: 8px;
  }
`;

const CategoryMenu = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  flex: 1;
  padding: 4px 0;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  position: relative;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Custom scrollbar for better visibility */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(94, 251, 169, 0.3) 50%, 
      transparent 100%);
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }

  @media (max-width: 768px) {
    gap: 10px;
    padding: 6px 0;
    scroll-padding-inline: 8px;
  }
`;

const CategoryButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 12px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #5efba9 0%, #34d399 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
  color: ${props => props.active ? '#0f172a' : '#475569'};
  font-size: 14px;
  font-weight: ${props => props.active ? '700' : '600'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${props => props.active ? 'rgba(94, 251, 169, 0.5)' : 'rgba(226, 232, 240, 0.6)'};
  box-shadow: ${props => props.active 
    ? '0 4px 14px rgba(94, 251, 169, 0.35), 0 2px 4px rgba(94, 251, 169, 0.2)' 
    : '0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)'};
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  flex: 0 0 auto;
  max-width: none;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.3), 
      transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: ${props => props.active
    ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
    : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
    transform: translateY(-3px) scale(1.02);
    box-shadow: ${props => props.active
    ? '0 8px 20px rgba(94, 251, 169, 0.4), 0 4px 8px rgba(94, 251, 169, 0.25)'
    : '0 6px 16px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)'};
    border-color: ${props => props.active ? 'rgba(94, 251, 169, 0.8)' : 'rgba(94, 251, 169, 0.4)'};
    color: ${props => props.active ? '#0f172a' : '#1e293b'};
  }
  
  &:active {
    transform: translateY(-1px) scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 9px 14px;
    font-size: 12px;
    min-height: 36px;
    border-radius: 10px;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 11px;
    min-height: 34px;
    white-space: nowrap;
  }
`;

const LoadingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const SkeletonCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  height: 220px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @media (max-width: 768px) {
    padding: 12px;
    height: 180px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    height: 150px;
  }
`;

const SkeletonBox = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #f3f4f6;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const SkeletonText = styled.div`
  height: 16px;
  background: #f3f4f6;
  border-radius: 4px;
  width: ${props => props.width || '80%'};
  margin: 0 auto;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 14px;
`;

const BannerSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #f3f4f6 0%,
    #e5e7eb 50%,
    #f3f4f6 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

function Home() {
  const { banners: bannerData, loading: bannerLoading, error: bannerError } = useBannerImages();
  const { categories: dbCategories, loading: categoriesLoading } = useCategories();
  const { brands: dbBrands, loading: brandsLoading } = useBrands();
  const { products: dbProducts, loading: productsLoading } = useProducts();
  const { subcategoriesByCategory, loading: subcategoriesLoading } = useAllSubcategories();
  const { language, isArabic } = useLanguage();
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [selectedBrandCategory, setSelectedBrandCategory] = useState('all');
  const [selectedProductCategory, setSelectedProductCategory] = useState('all');
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const categoryMenuRef = useRef(null);
  const productCategoryMenuRef = useRef(null);
  const [salesData, setSalesData] = useState({});
  const [trendingSalesLoading, setTrendingSalesLoading] = useState(true);
  const getSalesCount = (productId) => {
    if (productId === null || productId === undefined) return 0;
    const direct = salesData[productId];
    if (typeof direct === 'number') return direct;
    const asString = salesData[String(productId)];
    if (typeof asString === 'number') return asString;
    return 0;
  };
  const isBrandActive = (brand) => {
    if (typeof brand?.active === 'boolean') return brand.active;
    if (typeof brand?.is_active === 'boolean') return brand.is_active;
    if (brand?.active === 1 || brand?.active === '1' || brand?.active === 'true') return true;
    if (brand?.is_active === 1 || brand?.is_active === '1' || brand?.is_active === 'true') return true;
    return false;
  };

  // Auto-rotate banners based on duration from database
  useEffect(() => {
    if (bannerData.length === 0) return;

    const currentBanner = bannerData[currentBannerIndex];
    // Parse duration (assuming it's in milliseconds or seconds as a string)
    let duration = 4000; // Default 4 seconds

    if (currentBanner && currentBanner.duration) {
      const parsedDuration = parseInt(currentBanner.duration, 10);
      if (!isNaN(parsedDuration)) {
        // If duration is less than 1000, assume it's in seconds, convert to milliseconds
        duration = parsedDuration < 1000 ? parsedDuration * 1000 : parsedDuration;
        // Ensure minimum 1 second (1000ms) and maximum 30 seconds (30000ms)
        duration = Math.max(1000, Math.min(30000, duration));
      }
    }

    const timeoutId = setTimeout(() => {
      setCurrentBannerIndex((prevIndex) =>
        (prevIndex + 1) % bannerData.length
      );
    }, duration);

    return () => clearTimeout(timeoutId);
  }, [bannerData, currentBannerIndex]);

  // Memoize categories computation
  const categories = useMemo(() => {
    if (dbCategories.length === 0) return [];
    const icons = ['🍔', '💄', '👗', '📱', '🌸', '🎮', '👶'];
    return dbCategories.slice(0, 12).map((cat, index) => ({
      id: cat.id,
      name: isArabic ? (cat.name_ar || cat.name_en || cat.name) : (cat.name_en || cat.name),
      icon: icons[index % icons.length],
      image: cat.img || null
    }));
  }, [dbCategories, isArabic]);

  // Map product categories to display format
  // Show all categories here to match header behavior.
  const displayProductCategories = useMemo(() => {
    return dbCategories.map(cat => ({
      id: cat.id,
      name: language === 'ar' ? (cat.name_ar || cat.name_en || cat.name) : (cat.name_en || cat.name),
      slug: slugify(cat.name_en || cat.name),
      img: cat.img
    }));
  }, [dbCategories, language]);

  // Fetch sales data for trending products using production-safe source.
  useEffect(() => {
    let cancelled = false;

    const loadProductData = async () => {
      if (dbProducts.length === 0) {
        setTrendingSalesLoading(false);
        setSalesData({});
        return;
      }
      
      const activeProductIds = dbProducts
        .filter(p => p.status === 'active')
        .map(p => p.id);
      
      if (activeProductIds.length === 0) {
        setTrendingSalesLoading(false);
        setSalesData({});
        return;
      }

      try {
        setTrendingSalesLoading(true);
        const sales = await fetchSalesDataPublicSafe(activeProductIds);

        if (!cancelled) {
          setSalesData(sales);
        }
      } catch (error) {
        console.error('Error loading product data:', error);
        if (!cancelled) {
          setSalesData({});
        }
      } finally {
        if (!cancelled) {
          setTrendingSalesLoading(false);
        }
      }
    };

    loadProductData();
    return () => {
      cancelled = true;
    };
  }, [dbProducts]);

  // Memoize trending products computation with category filtering
  const trendingProducts = useMemo(() => {
    let filteredProducts = dbProducts.filter(product => product.status === 'active');
    
    // Filter by category if not 'all'
    if (selectedProductCategory !== 'all') {
      const selectedCategoryId = String(selectedProductCategory);
      filteredProducts = filteredProducts.filter(product => {
        const productCategoryId = product.category_id ? String(product.category_id) : null;
        return productCategoryId === selectedCategoryId;
      });
    }
    
    // Pure best-sellers sorting:
    // 1) higher sales first, 2) name A-Z, 3) newer first.
    filteredProducts = [...filteredProducts].sort((a, b) => {
      const salesA = getSalesCount(a.id);
      const salesB = getSalesCount(b.id);
      if (salesB !== salesA) return salesB - salesA;

      const nameA = String(isArabic ? (a.name_ar || a.name || '') : (a.name || a.name_ar || '')).toLowerCase();
      const nameB = String(isArabic ? (b.name_ar || b.name || '') : (b.name || b.name_ar || '')).toLowerCase();
      const nameComparison = nameA.localeCompare(nameB);
      if (nameComparison !== 0) return nameComparison;

      // Final deterministic fallback ordering when sales and names are equal.
      const dateA = new Date(a?.created_at || 0).getTime();
      const dateB = new Date(b?.created_at || 0).getTime();
      if (dateB !== dateA) return dateB - dateA;
      return String(b?.id || '').localeCompare(String(a?.id || ''));
    });
    
    return filteredProducts
      .slice(0, 10)
      .map(product => {
        const productBrand = dbBrands.find(brand => brand.id === product.brand_id);
        const discount = product.dis && product.dis !== 'no' ? parseFloat(product.dis) : null;
        const discountedPrice = discount && product.price ? (product.price * (1 - discount / 100)).toFixed(2) : null;

        return {
          id: product.id,
          title: product.name,
          name: product.name,
          name_ar: product.name_ar,
          price: discount ? discountedPrice : product.price,
          originalPrice: discount ? product.price : null,
          discount,
          currency: product.currency,
          image: product.img || product.images?.[0] || 'https://via.placeholder.com/300x240',
          brand: productBrand?.name,
          seller: { id: product.brand_id || 1, name: productBrand?.name || 'Seller' }
        };
      });
  }, [dbProducts, dbBrands, selectedProductCategory, salesData, isArabic]);

  // Map categories to display format (same as header)
  // Show all categories here to match header behavior.
  const displayCategories = useMemo(() => {
    return dbCategories.map(cat => ({
      id: cat.id,
      name: language === 'ar' ? (cat.name_ar || cat.name_en || cat.name) : (cat.name_en || cat.name),
      slug: slugify(cat.name_en || cat.name),
      img: cat.img
    }));
  }, [dbCategories, language]);

  // Flatten and prepare subcategories for display with category slugs
  const displaySubcategories = useMemo(() => {
    if (!subcategoriesByCategory || Object.keys(subcategoriesByCategory).length === 0) {
      return [];
    }

    const flattened = [];
    Object.keys(subcategoriesByCategory).forEach(categoryId => {
      const subcategories = subcategoriesByCategory[categoryId];
      const category = dbCategories.find(cat => String(cat.id) === String(categoryId));
      
      if (category) {
        const categorySlug = slugify(category.name_en || category.name);
        subcategories.forEach(subcat => {
          flattened.push({
            ...subcat,
            categorySlug
          });
        });
      }
    });

    return flattened.slice(0, 12); // Limit to 12 subcategories
  }, [subcategoriesByCategory, dbCategories]);


  // Filter and limit brands shown on home page based on selected category
  const displayedBrands = useMemo(() => {
    // Ensure only active brands are shown
    let filteredBrands = dbBrands.filter(isBrandActive);
    
    // Filter by category if not 'all'
    if (selectedBrandCategory !== 'all') {
      // Convert to string for consistent comparison (handles UUID, number, or string)
      const selectedCategoryId = String(selectedBrandCategory);
      
      // Get brand IDs that have products in the selected category
      const brandIdsInCategory = [...new Set(
        dbProducts
          .filter(product => {
            const productCategoryId = product.category_id ? String(product.category_id) : null;
            return productCategoryId === selectedCategoryId && product.status === 'active';
          })
          .map(product => product.brand_id ? String(product.brand_id) : null)
          .filter(Boolean)
      )];
      
      // Filter brands to only include those with products in the selected category
      filteredBrands = dbBrands.filter(brand => {
        const brandId = brand.id ? String(brand.id) : null;
        return brandId && brandIdsInCategory.includes(brandId);
      });
    }
    
    // Filter by search term if provided
    if (brandSearchTerm.trim()) {
      const searchLower = brandSearchTerm.toLowerCase().trim();
      filteredBrands = filteredBrands.filter(brand => {
        const brandName = brand.name ? brand.name.toLowerCase() : '';
        return brandName.includes(searchLower);
      });
    }
    
    return filteredBrands.slice(0, 12);
  }, [dbBrands, dbProducts, selectedBrandCategory, brandSearchTerm]);

  return (
    <Container>
      <BannerContainer>
        {bannerLoading ? (
          <BannerSkeleton />
        ) : bannerError ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#fee2e2', color: '#dc2626', padding: '20px' }}>
            Error loading banners: {bannerError}
          </div>
        ) : bannerData.length > 0 ? (
          <>
            <BannerSlideStack>
              {/* Only render current and next banner for better performance */}
              {bannerData.map((banner, index) => {
                const isCurrentOrNext = index === currentBannerIndex || index === (currentBannerIndex + 1) % bannerData.length;
                if (!isCurrentOrNext) return null;

                return (
                  <BannerImage
                    key={banner.id || index}
                    src={banner.img}
                    alt={banner.name || `Banner ${index + 1}`}
                    active={index === currentBannerIndex}
                    loading={index === currentBannerIndex ? 'eager' : 'lazy'}
                  />
                );
              })}
            </BannerSlideStack>
            {/* Link overlay for current banner - only shown when banner has URL */}
            {(() => {
              const currentBanner = bannerData[currentBannerIndex];
              const hasUrl = currentBanner?.url && currentBanner.url.trim() !== '';
              const isExternalUrl = hasUrl && (currentBanner.url.startsWith('http://') || currentBanner.url.startsWith('https://'));

              if (hasUrl) {
                if (isExternalUrl) {
                  return (
                    <BannerLink
                      href={currentBanner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      hasUrl={true}
                      aria-label={`Link to ${currentBanner.name || 'external site'}`}
                    />
                  );
                } else {
                  return (
                    <Link
                      to={currentBanner.url}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1,
                        textDecoration: 'none'
                      }}
                      aria-label={`Link to ${currentBanner.name || 'page'}`}
                    />
                  );
                }
              }
              return null;
            })()}
            <BannerDots>
              {bannerData.map((banner, index) => (
                <Dot
                  key={banner.id || index}
                  active={index === currentBannerIndex}
                  onClick={() => setCurrentBannerIndex(index)}
                />
              ))}
            </BannerDots>
          </>
        ) : (
          <BannerSkeleton />
        )}
      </BannerContainer>

      <Section>
        <SectionHeader>
          <ViewAllWrapper>
            <ViewAllLink to="/brands">
              {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
            </ViewAllLink>
          </ViewAllWrapper>
          <TitleWrapper>
            <SectionTitle>{isArabic ? 'العلامات التجارية المميزة' : 'Featured Brands'}</SectionTitle>
          </TitleWrapper>
        </SectionHeader>
        
        {/* Category Tabs - Same pattern as header */}
        {!brandsLoading && !categoriesLoading && displayCategories.length > 0 && (
          <CategoryBar>
            <CategoryContent>
              <CategoryMenu ref={categoryMenuRef}>
                <CategoryButton
                  active={selectedBrandCategory === 'all'}
                  onClick={() => setSelectedBrandCategory('all')}
                >
                  {language === 'ar' ? 'الكل' : 'All'}
                </CategoryButton>
                {displayCategories.map((category, index) => (
                  <CategoryButton
                    key={category.id || index}
                    active={selectedBrandCategory === category.id}
                    onClick={() => setSelectedBrandCategory(category.id)}
                  >
                    {category.name}
                  </CategoryButton>
                ))}
              </CategoryMenu>
            </CategoryContent>
          </CategoryBar>
        )}
        
        {/* Search Bar */}
        <SearchContainer>
          <SearchBar>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder={isArabic ? 'ابحث عن العلامات التجارية...' : 'Search brands...'}
              value={brandSearchTerm}
              onChange={(e) => setBrandSearchTerm(e.target.value)}
            />
            {brandSearchTerm && (
              <ClearButton
                onClick={() => setBrandSearchTerm('')}
                title={isArabic ? 'مسح البحث' : 'Clear search'}
              >
                <FiX size={18} />
              </ClearButton>
            )}
          </SearchBar>
          {brandSearchTerm && (
            <SearchResults>
              {isArabic 
                ? `تم العثور على ${displayedBrands.length} ${displayedBrands.length === 1 ? 'علامة تجارية' : 'علامات تجارية'} تطابق "${brandSearchTerm}"`
                : `Found ${displayedBrands.length} ${displayedBrands.length === 1 ? 'brand' : 'brands'} matching "${brandSearchTerm}"`
              }
            </SearchResults>
          )}
        </SearchContainer>
        
        {brandsLoading ? (
          <LoadingGrid>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <SkeletonCard key={i}>
                <SkeletonBox />
                <SkeletonText width="60%" />
              </SkeletonCard>
            ))}
          </LoadingGrid>
        ) : displayedBrands.length > 0 ? (
          <BrandsGrid>
            {displayedBrands.map((brand) => (
              <BrandCard key={brand.id} to={`/${slugify(brand.name)}`}>
                <BrandImageContainer>
                  {brand.img && brand.img.trim() !== '' ? (
                    <BrandImage src={brand.img} alt={brand.name || 'Brand'} loading="lazy" />
                  ) : (
                    <BrandIcon>🏷️</BrandIcon>
                  )}
                </BrandImageContainer>
                <BrandName>{brand.name}</BrandName>
              </BrandCard>
            ))}
          </BrandsGrid>
        ) : (
          <LoadingMessage>{isArabic ? 'لا توجد علامات تجارية متاحة' : 'No brands available'}</LoadingMessage>
        )}
        
        <ViewAllWrapperMobile>
          <ViewAllLink to="/brands">
            {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
          </ViewAllLink>
        </ViewAllWrapperMobile>
      </Section>

      <Section>
        <SectionHeader>
          <ViewAllWrapper>
            <ViewAllLink to="/categories/browse">
              {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
            </ViewAllLink>
          </ViewAllWrapper>
          <TitleWrapper>
            <SectionTitle>{isArabic ? 'تسوق حسب الفئة' : 'Shop By Category'}</SectionTitle>
          </TitleWrapper>
        </SectionHeader>
        {categoriesLoading ? (
          <LoadingGrid>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <SkeletonCard key={i}>
                <SkeletonBox />
                <SkeletonText width="70%" />
              </SkeletonCard>
            ))}
          </LoadingGrid>
        ) : categories.length > 0 ? (
          <Suspense fallback={<LoadingGrid>{[1, 2, 3, 4].map(i => <SkeletonCard key={i}><SkeletonBox /><SkeletonText /></SkeletonCard>)}</LoadingGrid>}>
            <CategoriesGrid>
              {categories.map(category => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </CategoriesGrid>
          </Suspense>
        ) : (
          <LoadingMessage>{isArabic ? 'لا توجد فئات متاحة' : 'No categories available'}</LoadingMessage>
        )}
        
        <ViewAllWrapperMobile>
          <ViewAllLink to="/categories/browse">
            {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
          </ViewAllLink>
        </ViewAllWrapperMobile>
      </Section>

      <Section>
        <SectionHeader>
          <ViewAllWrapper>
            <ViewAllLink to="/categories/browse">
              {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
            </ViewAllLink>
          </ViewAllWrapper>
          <TitleWrapper>
            <SectionTitle>{isArabic ? 'الفئات الفرعية' : 'Subcategories'}</SectionTitle>
          </TitleWrapper>
        </SectionHeader>
        {subcategoriesLoading || categoriesLoading ? (
          <LoadingGrid>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <SkeletonCard key={i}>
                <SkeletonBox />
                <SkeletonText width="70%" />
              </SkeletonCard>
            ))}
          </LoadingGrid>
        ) : displaySubcategories.length > 0 ? (
          <Suspense fallback={<LoadingGrid>{[1, 2, 3, 4].map(i => <SkeletonCard key={i}><SkeletonBox /><SkeletonText /></SkeletonCard>)}</LoadingGrid>}>
            <CategoriesGrid>
              {displaySubcategories.map(subcategory => (
                <SubcategoryCard 
                  key={subcategory.id} 
                  subcategory={subcategory} 
                  categorySlug={subcategory.categorySlug}
                />
              ))}
            </CategoriesGrid>
          </Suspense>
        ) : (
          <LoadingMessage>{isArabic ? 'لا توجد فئات فرعية متاحة' : 'No subcategories available'}</LoadingMessage>
        )}
        
        <ViewAllWrapperMobile>
          <ViewAllLink to="/categories/browse">
            {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
          </ViewAllLink>
        </ViewAllWrapperMobile>
      </Section>

      <Section>
        <SectionHeader>
          <ViewAllWrapper>
            <ViewAllLink to="/products">
              {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
            </ViewAllLink>
          </ViewAllWrapper>
          <TitleWrapper>
            <SectionTitle>{isArabic ? 'المنتجات الرائجة' : 'Trending Products'}</SectionTitle>
          </TitleWrapper>
        </SectionHeader>
        
        {/* Category Tabs for Products */}
        {!productsLoading && !categoriesLoading && displayProductCategories.length > 0 && (
          <CategoryBar>
            <CategoryContent>
              <CategoryMenu ref={productCategoryMenuRef}>
                <CategoryButton
                  active={selectedProductCategory === 'all'}
                  onClick={() => setSelectedProductCategory('all')}
                >
                  {language === 'ar' ? 'الكل' : 'All'}
                </CategoryButton>
                {displayProductCategories.map((category, index) => (
                  <CategoryButton
                    key={category.id || index}
                    active={selectedProductCategory === category.id}
                    onClick={() => setSelectedProductCategory(category.id)}
                  >
                    {category.name}
                  </CategoryButton>
                ))}
              </CategoryMenu>
            </CategoryContent>
          </CategoryBar>
        )}
        
        {productsLoading || trendingSalesLoading ? (
          <ProductsGrid>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
              <SkeletonCard key={i} style={{ height: '320px' }}>
                <SkeletonBox style={{ aspectRatio: '1 / 1', marginBottom: '12px' }} />
                <SkeletonText width="90%" style={{ marginBottom: '8px' }} />
                <SkeletonText width="50%" />
              </SkeletonCard>
            ))}
          </ProductsGrid>
        ) : trendingProducts.length > 0 ? (
          <Suspense fallback={<ProductsGrid>{[1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} style={{ height: '320px' }}><SkeletonBox /><SkeletonText /></SkeletonCard>)}</ProductsGrid>}>
            <ProductsGrid>
              {trendingProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </ProductsGrid>
          </Suspense>
        ) : (
          <LoadingMessage>{isArabic ? 'لا توجد منتجات متاحة بعد' : 'No products available yet'}</LoadingMessage>
        )}
        
        <ViewAllWrapperMobile>
          <ViewAllLink to="/products">
            {isArabic ? 'عرض الكل' : 'View All'} <FiChevronRight />
          </ViewAllLink>
        </ViewAllWrapperMobile>
      </Section>
    </Container>
  );
}

export default Home;
